/**
 * Settings → Custom Fields → a field → "Shows on" (owner ruling 2026-10-15).
 *
 * One item page tab (or none), plus any of: the item page's "At a glance"
 * column, the quick panel, and the item list's default columns. A small
 * picture of the item page, the quick panel and the list marks where the
 * field will appear. Item fields only; a child field shows with its parent.
 */

import type { Attribute, CustomFieldPlacement, CustomFieldPlacementTab } from '@/types/prioritization';
import { PLACEMENT_SPOTS, PLACEMENT_TABS, placementOf, placementSummary, type PlacementSpot } from '@/lib/customFieldPlacement';

const ON = 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-pri)] font-medium';
const OFF = 'border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]';

export function CustomFieldPlacementControl({
  field,
  parentLabel,
  onChange,
}: {
  field: Partial<Attribute>;
  parentLabel?: string;
  onChange: (placement: CustomFieldPlacement) => void;
}) {
  if ((field.objectType ?? 'item') !== 'item') return null;

  if (field.parentKey) {
    return (
      <div className="border border-[var(--rule)] p-4" data-testid="custom-field-shows-on">
        <p className="text-sm font-medium text-[var(--text-pri)]">Shows on</p>
        <p className="mt-0.5 text-xs text-[var(--text-ter)]">
          With {parentLabel ?? 'its parent'}, once that has a value. Change where {parentLabel ?? 'the parent'} shows to move both.
        </p>
      </div>
    );
  }

  const p = placementOf(field);
  const setTab = (tab: CustomFieldPlacementTab) => onChange({ ...p, tab });
  const toggle = (spot: PlacementSpot) => onChange({ ...p, [spot]: !p[spot] });
  const label = field.label?.trim() || 'This field';

  return (
    <div className="border border-[var(--rule)] p-4" data-testid="custom-field-shows-on">
      <p className="text-sm font-medium text-[var(--text-pri)]">Shows on</p>
      <p className="mt-0.5 text-xs text-[var(--text-ter)]">Where people see and fill in this field. Now: {placementSummary(field)}.</p>

      <div className="mt-3 grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
        <div className="space-y-3">
          <div>
            <p className="mb-1.5 text-xs text-[var(--text-sec)]">Item page tab</p>
            <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Item page tab">
              {PLACEMENT_TABS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  role="radio"
                  aria-checked={p.tab === t.value}
                  onClick={() => setTab(t.value)}
                  className={`border px-2.5 py-1 text-xs ${p.tab === t.value ? ON : OFF}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-1.5 text-xs text-[var(--text-sec)]">Also</p>
            <div className="space-y-1.5">
              {PLACEMENT_SPOTS.map((s) => (
                <label key={s.value} className="flex cursor-pointer items-start gap-2 text-sm text-[var(--text-sec)]">
                  <input type="checkbox" className="mt-0.5" checked={p[s.value]} onChange={() => toggle(s.value)} />
                  <span>
                    {s.label}
                    <span className="block text-xs text-[var(--text-ter)]">{s.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <PlacementPreview placement={p} label={label} />
      </div>
    </div>
  );
}

/** A small picture of the item page, the quick panel and the list, with this field marked. */
function PlacementPreview({ placement: p, label }: { placement: CustomFieldPlacement; label: string }) {
  const mark = 'bg-[var(--slate)] text-[var(--bg-raised)]';
  const ghost = 'bg-[var(--hover-tint)]';
  const tabs = PLACEMENT_TABS.filter((t) => t.value !== 'none');
  return (
    <div className="space-y-2 text-[10px] text-[var(--text-ter)]" aria-label="Where it shows" data-testid="custom-field-placement-preview">
      <div className="border border-[var(--rule)] bg-[var(--surface)] p-2">
        <p className="mb-1 font-mono uppercase tracking-wider">Item page</p>
        <div className="mb-1.5 flex gap-1">
          {tabs.map((t) => (
            <span key={t.value} className={`truncate px-1 py-0.5 ${p.tab === t.value ? mark : 'border border-[var(--rule)]'}`}>
              {t.value === 'delivery' ? 'Planning' : t.label}
            </span>
          ))}
        </div>
        <div className="flex gap-1.5">
          <div className="flex-1 space-y-1">
            <div className={`h-2 ${ghost}`} />
            <div className={`h-2 w-3/4 ${ghost}`} />
            {p.tab !== 'none' ? <div className={`truncate px-1 py-0.5 ${mark}`}>{label}</div> : <div className={`h-2 w-1/2 ${ghost}`} />}
          </div>
          <div className="w-[38%] space-y-1 border-l border-[var(--rule)] pl-1.5">
            <p className="font-mono uppercase">At a glance</p>
            <div className={`h-1.5 ${ghost}`} />
            {p.glance && <div className={`truncate px-1 py-0.5 ${mark}`}>{label}</div>}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="border border-[var(--rule)] bg-[var(--surface)] p-2">
          <p className="mb-1 font-mono uppercase tracking-wider">Quick panel</p>
          <div className={`mb-1 h-1.5 ${ghost}`} />
          {p.quickPanel ? <div className={`truncate px-1 py-0.5 ${mark}`}>{label}</div> : <div className={`h-1.5 w-2/3 ${ghost}`} />}
        </div>
        <div className="border border-[var(--rule)] bg-[var(--surface)] p-2">
          <p className="mb-1 font-mono uppercase tracking-wider">Item list</p>
          <div className="flex gap-0.5">
            <div className={`h-3 flex-1 ${ghost}`} />
            <div className={`h-3 flex-1 ${ghost}`} />
            {p.listColumn && <div className={`truncate px-0.5 ${mark}`}>{label}</div>}
          </div>
        </div>
      </div>
      {p.tab === 'none' && !p.glance && !p.quickPanel && !p.listColumn && (
        <p className="text-[var(--mustard)]">Shown nowhere on screen: values still arrive by import and can still be filtered on in the item list.</p>
      )}
    </div>
  );
}
